import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { loadReadOnlyState } from "../agentur/einstellungen/data";
import { api } from "../../data";
import { Dialog, PillView, useAction } from "../../components/ui";
import {
  activateSubscription, closeRequest, deleteAccount, extendTrial, grantTrial, resetSecondFactor, setChef, setSuspended, type AgenturData, type SubscriptionRequestRow,
} from "./data";
import { useDeletions, useLifecycleLog, useRequests } from "./hooks";
import { berlinDate, berlinDateTime, berlinMittag, berlinTagesende, deletionPill, laufzeitAbgelaufen, lifecycleActionLabel, orgStatusPill, planOverage, utcDate, utcDateTime, type OrgOverviewRow } from "./model";
import { t } from "../../lib/sprache";
import { DemoHinweis, Fehler, Laden } from "./parts";

/**
 * Verwaltung durch den Plattform-Betrieb (0057): Abo freischalten, Testzugang ohne Zahlungsdaten geben (0205, ersetzt „Testphase verlängern“), sperren und entsperren,
 * Tarifanfragen der Agenturen, Admin neu setzen, Anträge auf Kontolöschung und das Protokoll des Lebenszyklus.
 * Jede Aktion prüft die Datenbank selbst und steht im Protokoll der Agentur.
 */

const intervalText = (i: string | null | undefined) => (i === "year" ? "jährlich" : "monatlich");

/** Abo und Status einer Agentur (Agentur-Detail) */
export function AboSteuerung({ row, data }: { row: OrgOverviewRow; data: AgenturData }) {
  const { busy, error, run } = useAction();
  const requests = useRequests(row.org_id);
  const open = (requests.data ?? []).find((r) => !r.handled_at) ?? null;
  const stripe = !!data.subscription?.stripe_subscription_id;
  const [ask, setAsk] = useState<"abo" | "test" | "sperre" | null>(null);
  const [plan, setPlan] = useState(open?.plan_key ?? row.plan_key ?? data.plans[0]?.key ?? "");
  const [interval, setBilling] = useState<"month" | "year">(open?.billing_interval ?? (row.billing_interval === "year" ? "year" : "month"));
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [days, setDays] = useState("14");
  const [accept, setAccept] = useState(false);
  const [nowMs] = useState(() => Date.now());
  const abgelaufen = laufzeitAbgelaufen(row.current_period_end, nowMs);
  const close = () => { setAsk(null); setNote(""); setAccept(false); };
  const planName = data.plans.find((p) => p.key === plan)?.name ?? plan;
  const over = plan ? planOverage(row, plan, data.entitlements) : [];
  const trialState = row.status === "trial" || (row.status === "suspended" && row.subscription_status === "trialing");

  return (
    <section className="panel stack" aria-labelledby="abo-steuerung-titel">
      <h2 id="abo-steuerung-titel">Abo und Status</h2>
      <div className="row">
        <PillView pill={orgStatusPill(row.status)} />
        <span>{row.plan_name ?? "ohne Tarif"} · {intervalText(row.billing_interval)}{row.current_period_end && !abgelaufen ? ` · Laufzeit bis ${utcDate(row.current_period_end)}` : ""}</span>
        {abgelaufen && <span className="pill error"><span className="pill-zeichen">!</span> Laufzeit abgelaufen am {utcDate(row.current_period_end!)}{stripe ? "" : " · verlängern oder sperren"}</span>}
      </div>
      {open && (
        <div className="notice" role="status">
          Anfrage vom {utcDate(open.created_at)}: {open.plan_name ?? open.plan_key}, {intervalText(open.billing_interval)}{open.requested_by_email ? ` · von ${open.requested_by_email}` : ""}
          {open.note ? ` · „${open.note}“` : ""}
        </div>
      )}
      {stripe && <small>Dieses Abo führt Stripe. Tarif und Status ändert der Webhook; freischalten hier ist gesperrt.</small>}
      <div className="row">
        <button className="primary" type="button" disabled={stripe || !!busy} onClick={() => setAsk("abo")}>Abo freischalten</button>
        <button type="button" disabled={!trialState || !!busy} onClick={() => setAsk("test")}>Testzugang geben</button>
        {row.status === "suspended"
          ? <button type="button" disabled={!!busy} aria-busy={busy === "entsperren"} onClick={() => void run("entsperren", () => setSuspended(api, row.org_id, false, null), "Agentur entsperrt.")}>Entsperren</button>
          : <button type="button" className="danger" disabled={row.status === "cancelled" || !!busy} onClick={() => setAsk("sperre")}>Sperren</button>}
        {open && (
          <button type="button" disabled={!!busy} onClick={() => void run("anfrage-zu", () => closeRequest(api, open.id, "contacted", "Kontakt aufgenommen"), "Anfrage als bearbeitet vermerkt.")}>
            Anfrage: Kontakt aufgenommen
          </button>
        )}
      </div>
      {error && !ask && <p className="tk-error" role="alert">{error}</p>}

      <Dialog open={ask === "abo"} onClose={close} title="Abo freischalten">
        <div className="stack">
          <p>Setzt Abo und Agentur gemeinsam auf „aktiv“ (auch aus der Nur-Lesen-Phase), schließt eine offene Anfrage und schickt dem Admin eine Bestätigung.</p>
          <label>
            Tarif
            <select value={plan} onChange={(e) => setPlan(e.target.value)}>
              {data.plans.map((p) => <option key={p.key} value={p.key}>{p.name}{p.active ? "" : " · nicht mehr angeboten"}</option>)}
            </select>
          </label>
          <label>
            Abrechnung
            <select value={interval} onChange={(e) => setBilling(e.target.value === "year" ? "year" : "month")}>
              <option value="month">monatlich</option>
              <option value="year">jährlich</option>
            </select>
          </label>
          <label>
            Laufzeit bis (UTC)
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            <small>Leer: {interval === "year" ? "ein Jahr" : "ein Monat"} ab heute. Höchstens drei Jahre.</small>
          </label>
          <label>
            Notiz fürs Protokoll
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Rechnung 2026-17, bezahlt per Überweisung" />
          </label>
          {over.length > 0 && (
            <div className="notice stack" role="status">
              <span>Die Agentur nutzt mehr, als {planName} erlaubt: {over.join("; ")}. Bestehende bleiben, neue lassen sich erst nach dem Abbau anlegen; bezahlt würde aber der kleinere Tarif.</span>
              <label className="row tk-check">
                <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} />
                <span>Trotzdem freischalten (Grund in der Notiz, mindestens 5 Zeichen)</span>
              </label>
            </div>
          )}
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button className="primary" type="button" disabled={!plan || !!busy || (over.length > 0 && (!accept || note.trim().length < 5))} aria-busy={busy === "abo"}
              onClick={() => void run("abo", () => activateSubscription(api, row.org_id, { plan, interval, periodEnd: end || null, note, acceptOverage: over.length > 0 && accept }), `Abo ${planName} freigeschaltet.`).then((ok) => ok && close())}>
              {busy === "abo" ? "Wird freigeschaltet …" : `${planName} freischalten`}
            </button>
            <button type="button" onClick={close}>Abbrechen</button>
          </div>
        </div>
      </Dialog>

      <Dialog open={ask === "test"} onClose={close} title="Testzugang ohne Zahlungsdaten">
        <div className="stack">
          <p>Kostenloser Testzugang ab heute für die gewählte Dauer, ohne Zahlungsdaten. Eine laufende Testphase wird ersetzt, eine abgelaufene läuft wieder. Hat das Konto schon ein Probeabo bei Stripe, endet es am selben Tag; vorher wird nichts abgebucht.</p>
          <label>
            Tage
            <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} inputMode="numeric" />
          </label>
          <label>
            Notiz fürs Protokoll
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Messe, Wunsch per Mail" />
          </label>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button className="primary" type="button" disabled={!!busy} aria-busy={busy === "test"}
              onClick={() => void run("test", () => grantTrial(api, row.org_id, Number(days), note), `Testzugang für ${days} Tage ab heute.`).then((ok) => ok && close())}>
              {busy === "test" ? "Wird gespeichert …" : "Testzugang geben"}
            </button>
            <button type="button" onClick={close}>Abbrechen</button>
          </div>
        </div>
      </Dialog>

      <Dialog open={ask === "sperre"} onClose={close} title="Agentur sperren">
        <div className="stack">
          <p>Die Agentur wird nur lesbar: Der Admin liest und exportiert weiter und sieht den Grund; Manager haben keinen Zugang. Entsperren geht jederzeit.</p>
          <label>
            Grund (sieht der Admin)
            <textarea rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Zahlung per Rechnung seit 30 Tagen offen" />
          </label>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button className="danger" type="button" disabled={note.trim().length < 5 || !!busy} aria-busy={busy === "sperre"}
              onClick={() => void run("sperre", () => setSuspended(api, row.org_id, true, note), "Agentur gesperrt.").then((ok) => ok && close())}>
              {busy === "sperre" ? "Wird gesperrt …" : "Sperren"}
            </button>
            <button type="button" onClick={close}>Abbrechen</button>
          </div>
        </div>
      </Dialog>
    </section>
  );
}

/**
 * 0425: „Nur-Lesen statt Sperre“ je Agentur. Für Bestandskunden, deren Vertrag vor dem Gültig-ab-Datum der AGB vom 23.10.2026 endet:
 * nach dem Vertragsende gilt dann die alte Regel (Nur-Lesen-Phase) statt der Portal-Sperre. Steht im Protokoll der Agentur.
 */
export function NurLesenStattSperre({ row }: { row: OrgOverviewRow }) {
  const { busy, error, run } = useAction();
  const stand = useQuery({ queryKey: ["org_read_only", row.org_id, "betrieb"], queryFn: () => loadReadOnlyState(api, row.org_id) });
  const an = !!stand.data?.readonly_instead_of_lock;
  const schalten = (on: boolean) => void run("nurlesen", async () => {
    await api.rpc("platform_set_readonly_instead_of_lock", { p_org: row.org_id, p_on: on });
    await stand.refetch();
  }, on ? "Nach dem Vertragsende gilt für diese Agentur die Nur-Lesen-Phase." : "Nach dem Vertragsende ist das Portal wieder gesperrt.");
  return (
    <section className="panel stack" aria-labelledby="nurlesen-titel">
      <h2 id="nurlesen-titel">Nach Vertragsende: Nur-Lesen statt Sperre</h2>
      <p>Nur für Bestandskunden, deren Vertrag vor dem Gültig-ab-Datum der AGB vom 23.10.2026 endet: Nach dem Vertragsende bleibt das Portal dann wie früher nur lesbar, statt gesperrt zu werden. Ohne Zustimmung eines Admins zur AGB vom 23.10.2026 gilt die alte Regel ohnehin (0436); der Merker fällt mit der Zustimmung weg.</p>
      <p>Stand: {stand.isPending ? "…" : an ? "Nur-Lesen (Merker gesetzt)" : stand.data?.old_terms_rule ? "Nur-Lesen (keine Zustimmung zur AGB vom 23.10.2026)" : "Sperre (neue Regel)"}{stand.data?.ended ? " · Vertrag beendet" : ""}.</p>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button type="button" disabled={!!busy || stand.isPending} aria-busy={busy === "nurlesen"} onClick={() => schalten(!an)}>
          {an ? "Wieder sperren (neue Regel)" : "Nur-Lesen statt Sperre setzen"}
        </button>
      </div>
    </section>
  );
}

/**
 * 0439: Anbieterwechsel und Datenübertragung (AGB 24.10.2026). Bis zum Ende des Wechsels läuft CreatorOffice im vereinbarten Umfang
 * weiter: kein Vertragsende, keine Sperre, kein Nur-Lesen wegen des Vertragsendes (Pause und offene Zahlung bleiben). Höchstens
 * 7 Monate ab Eingang des Verlangens. Ablauf: docs/betrieb/ANBIETERWECHSEL.md. Steht im Protokoll der Agentur.
 */
export function Anbieterwechsel({ row }: { row: OrgOverviewRow }) {
  const { busy, error, run } = useAction();
  const stand = useQuery({ queryKey: ["org_read_only", row.org_id, "betrieb"], queryFn: () => loadReadOnlyState(api, row.org_id) });
  const [bis, setBis] = useState("");
  const [eingang, setEingang] = useState("");
  const [notiz, setNotiz] = useState("");
  const laeuft = !!stand.data?.switching;
  const setzen = () => void run("wechsel", async () => {
    // 0441 (W-3): Tage in der Zeit des Betriebs (Europe/Berlin), Ende = Tagesende
    await api.rpc("platform_set_switching", { p_org: row.org_id, p_until: berlinTagesende(bis), p_note: notiz.trim() || null, p_requested_at: eingang ? berlinMittag(eingang) : null });
    setBis(""); setNotiz("");
    await stand.refetch();
  }, laeuft ? "Anbieterwechsel verlängert." : "Anbieterwechsel gesetzt: bis dahin kein Vertragsende.");
  const beenden = () => void run("wechsel_ende", async () => {
    await api.rpc("platform_set_switching", { p_org: row.org_id, p_until: null, p_note: notiz.trim() || null });
    await stand.refetch();
  }, "Anbieterwechsel beendet: ab jetzt gilt das Vertragsende wieder.");
  return (
    <section className="panel stack" aria-labelledby="wechsel-titel">
      <h2 id="wechsel-titel">Anbieterwechsel bis</h2>
      <p>Während eines Anbieterwechsels (AGB vom 24.10.2026) läuft CreatorOffice weiter: kein Vertragsende, keine Sperre, kein Nur-Lesen wegen des Vertragsendes. Pause und offene Zahlung bleiben. Höchstens 7 Monate ab dem Eingang des Verlangens; die Fristen danach zählen ab dem Ende des Wechsels. Eine vorgemerkte Kündigung, die vorher wirksam würde, schiebt das Setzen auf das Ende des Wechsels (Stripe bzw. PayPal nachziehen). Alle Zeiten in Europe/Berlin.</p>
      <p>
        Stand: {stand.isPending ? "…" : laeuft ? `läuft bis ${berlinDateTime(stand.data!.switching_until!)}` : stand.data?.switching_until ? `beendet am ${berlinDateTime(stand.data.switching_until)}` : "kein Wechsel"}
        {stand.data?.switching_requested_at ? ` · Eingang ${berlinDate(stand.data.switching_requested_at)}` : ""}
      </p>
      {stand.data?.switching_note && <p className="muted">Notiz: {stand.data.switching_note}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <label>Wechsel bis<input type="date" value={bis} onChange={(e) => setBis(e.target.value)} /></label>
        {!laeuft && <label>Eingang des Verlangens<input type="date" value={eingang} onChange={(e) => setEingang(e.target.value)} /></label>}
      </div>
      <label>Notiz (Ticket, neuer Anbieter, Absprachen)<textarea rows={2} value={notiz} onChange={(e) => setNotiz(e.target.value)} /></label>
      <div className="row">
        <button type="button" disabled={!!busy || !bis} aria-busy={busy === "wechsel"} onClick={setzen}>{laeuft ? "Wechsel verlängern" : "Wechsel setzen"}</button>
        {laeuft && <button type="button" disabled={!!busy} aria-busy={busy === "wechsel_ende"} onClick={beenden}>Wechsel beenden</button>}
      </div>
    </section>
  );
}

/** Admin neu setzen, wenn niemand mehr die Rolle ausübt (Befund 20) */
export function ChefSetzen({ row }: { row: OrgOverviewRow }) {
  const { busy, error, run } = useAction();
  const [openDialog, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [ticket, setTicket] = useState("");
  const [reason, setReason] = useState("");
  const [replace, setReplace] = useState("");
  const ok = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) && ticket.trim().length >= 3 && reason.trim().length >= 20;
  // RECHTE-11 (0085): in der Nur-Lesen-Phase nimmt die neue Person die Einladung an und liest Belege, Export und Abo
  const nurLesen = row.status === "suspended" || row.status === "cancelled";
  const nurLesenHinweis = "Die Agentur ist gesperrt oder gekündigt. Die neue Person kann die Einladung trotzdem annehmen und hat dann als Admin Zugriff auf Belege, Export und Abo, aber nur lesend.";
  return (
    <section className="panel stack" aria-labelledby="chef-titel">
      <h2 id="chef-titel">Admin neu setzen</h2>
      <p>
        Nur wenn niemand mehr die Rolle ausüben kann (Ausscheiden ohne Übergabe, Krankheit, Streit) und die Berechtigung belegt ist, etwa per Registerauszug oder Vollmacht.
        Hat die Person noch kein Konto, geht eine Einladung als Admin an die Adresse. Alle Admins bekommen eine Mitteilung; das Protokoll der Agentur hält Ticket und Prüfung fest.
      </p>
      <p>Admin heute: {row.chef_emails.length ? row.chef_emails.join(", ") : "keiner aktiv"}</p>
      {nurLesen && <div className="notice warning" role="status">{nurLesenHinweis}</div>}
      <div className="row"><button type="button" onClick={() => setOpen(true)}>Admin neu setzen</button></div>
      <Dialog open={openDialog} onClose={() => setOpen(false)} title="Admin neu setzen">
        <div className="stack">
          <label>E-Mail der neuen Person<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" /></label>
          <label>Ticketnummer<input value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="z. B. T-2026-118" /></label>
          <label>
            Identitätsprüfung (mindestens 20 Zeichen)
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Wer hat angefragt, womit ist die Berechtigung belegt?" />
          </label>
          <label>
            Bisherigen Admin ablösen (Zugang und Inhaber-Rolle enden sofort, auch wenn die neue Person erst eingeladen wird)
            <select value={replace} onChange={(e) => setReplace(e.target.value)}>
              <option value="">Niemanden ablösen</option>
              {row.chef_emails.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </label>
          {nurLesen && <p className="muted">{nurLesenHinweis}</p>}
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button className="primary" type="button" disabled={!ok || !!busy} aria-busy={busy === "chef"}
              onClick={() => void run("chef", () => setChef(api, row.org_id, { email, ticket, reason, replaceEmail: replace || null }), replace ? "Admin gesetzt bzw. eingeladen. Die abgelöste Person hat ab sofort keinen Zugang mehr; eine Eingeladene wird beim Annehmen Inhaber." : "Admin gesetzt bzw. eingeladen. Eine Eingeladene wird beim Annehmen Inhaber.")
                .then((r) => { if (r) { setOpen(false); setEmail(""); setTicket(""); setReason(""); setReplace(""); } })}>
              {busy === "chef" ? "Wird gesetzt …" : "Admin setzen"}
            </button>
            <button type="button" onClick={() => setOpen(false)}>Abbrechen</button>
          </div>
        </div>
      </Dialog>
    </section>
  );
}

/**
 * Zweiten Faktor zurücksetzen (0077, RECHTE-7): Handy verloren, Person ausgesperrt. Nur mit Ticket und beschriebener
 * Identitätsprüfung; Protokoll in jeder Agentur der Person, Mitteilung an deren Admin, Mail an die Person.
 */
export function FaktorZuruecksetzen() {
  const { busy, error, run } = useAction();
  const [openDialog, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [ticket, setTicket] = useState("");
  const [reason, setReason] = useState("");
  const ok = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) && ticket.trim().length >= 3 && reason.trim().length >= 20;
  return (
    <section className="panel stack" aria-labelledby="faktor-titel">
      <h2 id="faktor-titel">Zweiten Faktor zurücksetzen</h2>
      <p>
        Für Personen, die ihr Handy verloren haben und ohne Code nicht mehr ins Portal kommen. Nur nach belegter Identität (z. B. Rückruf
        über die bekannte Nummer und Ausweis). Die Person meldet sich danach nur mit Passwort an und richtet einen neuen Faktor ein; ihre
        Admin erfahren es per Glocke und Mail.
      </p>
      <div className="row"><button type="button" onClick={() => setOpen(true)}>Zweiten Faktor zurücksetzen</button></div>
      <Dialog open={openDialog} onClose={() => setOpen(false)} title="Zweiten Faktor zurücksetzen">
        <div className="stack">
          <label>E-Mail der Person<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" /></label>
          <label>Ticketnummer<input value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="z. B. T-2026-131" /></label>
          <label>
            Identitätsprüfung (mindestens 20 Zeichen)
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Wer hat angefragt, wie ist die Identität belegt?" />
          </label>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button className="danger" type="button" disabled={!ok || !!busy} aria-busy={busy === "faktor"}
              onClick={() => void run("faktor", () => resetSecondFactor(api, { email, ticket, reason }), "Zweiter Faktor zurückgesetzt. Die Person und ihre Admins werden informiert.")
                .then((r) => { if (r) { setOpen(false); setEmail(""); setTicket(""); setReason(""); } })}>
              {busy === "faktor" ? "Wird zurückgesetzt …" : "Zurücksetzen"}
            </button>
            <button type="button" onClick={() => setOpen(false)}>Abbrechen</button>
          </div>
        </div>
      </Dialog>
    </section>
  );
}

/** Seite „Anträge“: Tarifanfragen, Löschanträge mit Frist-Ampel, Protokoll des Lebenszyklus */
export function Antraege() {
  const requests = useRequests();
  const deletions = useDeletions();
  const log = useLifecycleLog();
  const { busy, error, run } = useAction();
  const [nowMs] = useState(() => Date.now());
  const [confirm, setConfirm] = useState<{ id: string; email: string | null } | null>(null);
  const openRequests = (requests.data ?? []).filter((r) => !r.handled_at);
  const done = (requests.data ?? []).filter((r) => r.handled_at).slice(0, 10);
  return (
    <>
      <title>Anträge · Plattform · CreatorOffice</title>
      <h1>Anträge</h1>
      {error && !confirm && <p className="tk-error" role="alert">{error}</p>}
      <FaktorZuruecksetzen />

      <section className="panel stack" aria-labelledby="tarif-titel">
        <h2 id="tarif-titel">Tarifanfragen · {openRequests.length} offen</h2>
        {requests.error ? <Fehler error={requests.error} /> : requests.isPending ? <Laden /> : (
          <div className="tablewrap">
            <table>
              <caption className="sr-only">Tarifanfragen der Agenturen</caption>
              <thead><tr><th scope="col">Eingang</th><th scope="col">Agentur</th><th scope="col">Wunsch</th><th scope="col">Stand</th><th scope="col">Aktion</th></tr></thead>
              <tbody>
                {[...openRequests, ...done].map((r: SubscriptionRequestRow) => (
                  <tr key={r.id}>
                    <td>{utcDateTime(r.created_at)}</td>
                    <td><Link to={`/betrieb/agenturen/${r.org_id}`}>{r.org_name}</Link>{r.requested_by_email && <small>{r.requested_by_email}</small>}</td>
                    <td>{r.plan_name ?? r.plan_key} · {intervalText(r.billing_interval)}{r.note && <small>{r.note}</small>}</td>
                    <td>{r.handled_at ? `${r.outcome === "activated" ? "Freigeschaltet" : r.outcome === "declined" ? "Abgelehnt" : "Kontakt aufgenommen"} am ${utcDate(r.handled_at)}` : <span className="pill warning"><span className="pill-zeichen">◷</span> Offen</span>}</td>
                    <td>
                      {!r.handled_at && (
                        <div className="row">
                          <Link className="btn primary" to={`/betrieb/agenturen/${r.org_id}`}>Freischalten</Link>
                          <button type="button" disabled={!!busy} onClick={() => void run(`abl-${r.id}`, () => closeRequest(api, r.id, "declined", null), "Anfrage abgelehnt.")}>Ablehnen</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!openRequests.length && !done.length && <tr><td colSpan={5}>Keine Tarifanfragen.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel stack" aria-labelledby="loesch-titel">
        <h2 id="loesch-titel">Anträge auf Kontolöschung</h2>
        <p>
          Die Person hat keinen Zugang mehr. Löschen entfernt das Anmeldekonto; verweisen Belege oder Protokolle darauf, wird es unkenntlich gemacht (Adresse, Name, Anmeldung).
          Nach der Karenzzeit erledigt der tägliche Lauf offene Anträge selbst. Frist nach DSGVO: ein Monat.
        </p>
        {deletions.error ? <Fehler error={deletions.error} /> : deletions.isPending ? <Laden /> : (
          <div className="tablewrap">
            <table>
              <caption className="sr-only">Anträge auf Kontolöschung</caption>
              <thead><tr><th scope="col">Eingang</th><th scope="col">Konto</th><th scope="col">Frist</th><th scope="col">Aktion</th></tr></thead>
              <tbody>
                {(deletions.data ?? []).map((d) => (
                  <tr key={d.user_id}>
                    <td>{utcDateTime(d.requested_at)}{d.reason && <small>Grund: {d.reason}</small>}</td>
                    <td>{d.email ?? "–"}{d.orgs && <small>{d.orgs}</small>}{d.blocked_reason && <small className="tk-error">{d.blocked_reason}</small>}</td>
                    <td><PillView pill={deletionPill(d, nowMs)} />{!d.processed_at && <small>automatisch ab {utcDate(d.auto_at)} · spätestens {utcDate(d.due_at)}</small>}</td>
                    <td>{!d.processed_at && <button className="danger" type="button" disabled={!!busy || !!d.blocked_reason} onClick={() => setConfirm({ id: d.user_id, email: d.email })}>Konto löschen</button>}</td>
                  </tr>
                ))}
                {(deletions.data ?? []).length === 0 && <tr><td colSpan={4}>Keine Anträge.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel stack" aria-labelledby="lauf-titel">
        <h2 id="lauf-titel">Lebenszyklus-Lauf · letzte Einträge</h2>
        <p>Täglicher Lauf: Kündigungen werden wirksam, Reduktion und Löschung werden angekündigt und ausgeführt. Fehler einer Agentur halten die anderen nicht auf und stehen hier.</p>
        {log.error ? <Fehler error={log.error} /> : log.isPending ? <Laden /> : (
          <div className="tablewrap">
            <table>
              <caption className="sr-only">Protokoll des Lebenszyklus</caption>
              <thead><tr><th scope="col">Zeitpunkt</th><th scope="col">Agentur</th><th scope="col">Ereignis</th><th scope="col">Einzelheiten</th></tr></thead>
              <tbody>
                {(log.data ?? []).map((l) => (
                  <tr key={l.id}>
                    <td>{utcDateTime(l.at)}</td>
                    <td>{l.org_name ?? "–"}</td>
                    <td>
                      {l.action === "error" ? <span className="pill error"><span className="pill-zeichen">!</span> {lifecycleActionLabel(l.action)}</span> : lifecycleActionLabel(l.action)}
                      {l.action === "storage_pending" && l.resolved_at && <small>{t(`erledigt am ${utcDateTime(l.resolved_at)} · ${l.resolved_detail?.deleted ?? 0} Dateien gelöscht`, `done on ${utcDateTime(l.resolved_at)} · ${l.resolved_detail?.deleted ?? 0} files deleted`)}</small>}
                    </td>
                    <td>{l.error ?? (Object.keys(l.detail ?? {}).length ? JSON.stringify(l.detail) : "–")}</td>
                  </tr>
                ))}
                {(log.data ?? []).length === 0 && <tr><td colSpan={4}>Noch keine Einträge.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} title="Konto endgültig löschen?">
        <div className="stack">
          <p>Das Anmeldekonto {confirm?.email ?? ""} wird gelöscht bzw. unkenntlich gemacht. Das lässt sich nicht rückgängig machen. Daten der Agenturen bleiben.</p>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button className="danger" type="button" disabled={!!busy} aria-busy={busy === "konto"}
              onClick={() => void run("konto", () => deleteAccount(api, confirm!.id), "Konto gelöscht.").then((r) => r && setConfirm(null))}>
              {busy === "konto" ? "Wird gelöscht …" : "Endgültig löschen"}
            </button>
            <button type="button" onClick={() => setConfirm(null)}>Abbrechen</button>
          </div>
        </div>
      </Dialog>
      <DemoHinweis />
    </>
  );
}
